/**
 * API service for reporting system
 */

// When using Vite proxy, we need to use relative paths
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

export interface ReportTemplate {
  id: number;
  name: string;
  description: string;
  report_type: string;
  site: number;
  is_active: boolean;
  created_at: string;
}

export interface ReportCalculation {
  id: number;
  calculation_name: string;
  calculation_type: string;
  scada_tag?: {
    id: number;
    tag_name: string;
    description: string;
    engineering_unit: string;
  };
  report_template: number;
  aggregation_method: string;
  reset_detection_enabled: boolean;
  is_active: boolean;
}

export interface DailyEnergyData {
  date: string;
  energy: number | null;
  reset_detected: boolean;
  data_points: number;
}

export interface EnergyTagData {
  tag_name: string;
  tag_description: string;
  tag_units: string;
  monthly_total: number;
  daily_breakdown: DailyEnergyData[];
  days_with_data: number;
}

export interface EnergySummaryResponse {
  month: number;
  year: number;
  energy_data: EnergyTagData[];
}

export interface ReportingOverviewResponse {
  report_templates: ReportTemplate[];
  recent_calculations: ReportCalculation[];
  permissions: {
    can_access_reports: boolean;
    can_manage_reports: boolean;
  };
}

export interface CalculationRunRequest {
  calculation_id: number;
  start_date: string;
  end_date: string;
}

export interface CalculationRunResponse {
  history_id: number;
  calculation: ReportCalculation;
  results: DailyEnergyData[];
  total_days: number;
  days_with_data: number;
}

class ReportingApiService {
  private baseURL: string;

  constructor() {
    this.baseURL = `${API_BASE_URL}/sites`;
  }

  private async fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
    const token = localStorage.getItem('access_token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    };

    const response = await fetch(`${this.baseURL}${url}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    return response;
  }

  /**
   * Get reporting dashboard overview
   */
  async getOverview(): Promise<ReportingOverviewResponse> {
    const response = await this.fetchWithAuth('/reports/dashboard/overview/');
    return response.json();
  }

  /**
   * Get energy summary for a specific month/year
   */
  async getEnergySummary(month?: number, year?: number): Promise<EnergySummaryResponse> {
    const params = new URLSearchParams();
    if (month) params.append('month', month.toString());
    if (year) params.append('year', year.toString());
    
    const response = await this.fetchWithAuth(`/reports/dashboard/energy-summary/?${params.toString()}`);
    return response.json();
  }

  /**
   * Run a calculation for a specific date range
   */
  async runCalculation(request: CalculationRunRequest): Promise<CalculationRunResponse> {
    const response = await this.fetchWithAuth('/reports/dashboard/run-calculation/', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return response.json();
  }

  /**
   * Get all report templates
   */
  async getReportTemplates(): Promise<ReportTemplate[]> {
    const response = await this.fetchWithAuth('/reports/templates/');
    return response.json();
  }

  /**
   * Get all report calculations
   */
  async getReportCalculations(): Promise<ReportCalculation[]> {
    const response = await this.fetchWithAuth('/reports/calculations/');
    return response.json();
  }

  /**
   * Create a new report template
   */
  async createReportTemplate(template: Partial<ReportTemplate>): Promise<ReportTemplate> {
    const response = await this.fetchWithAuth('/reports/templates/', {
      method: 'POST',
      body: JSON.stringify(template),
    });
    return response.json();
  }

  /**
   * Update a report template
   */
  async updateReportTemplate(id: number, template: Partial<ReportTemplate>): Promise<ReportTemplate> {
    const response = await this.fetchWithAuth(`/reports/templates/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(template),
    });
    return response.json();
  }

  /**
   * Delete a report template
   */
  async deleteReportTemplate(id: number): Promise<void> {
    await this.fetchWithAuth(`/reports/templates/${id}/`, {
      method: 'DELETE',
    });
  }

  /**
   * Create a new report calculation
   */
  async createReportCalculation(calculation: Partial<ReportCalculation>): Promise<ReportCalculation> {
    const response = await this.fetchWithAuth('/reports/calculations/', {
      method: 'POST',
      body: JSON.stringify(calculation),
    });
    return response.json();
  }

  /**
   * Update a report calculation
   */
  async updateReportCalculation(id: number, calculation: Partial<ReportCalculation>): Promise<ReportCalculation> {
    const response = await this.fetchWithAuth(`/reports/calculations/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(calculation),
    });
    return response.json();
  }

  /**
   * Delete a report calculation
   */
  async deleteReportCalculation(id: number): Promise<void> {
    await this.fetchWithAuth(`/reports/calculations/${id}/`, {
      method: 'DELETE',
    });
  }

  // Historical Data Management
  async getHistoricalDataSummary(siteId: number, params?: Record<string, any>): Promise<any> {
    const queryParams = new URLSearchParams({
      site_id: siteId.toString(),
      ...params
    });
    const response = await this.fetchWithAuth(`/reports/historical-data/summary/?${queryParams}`);
    return response.json();
  }

  async getMultiYearComparison(siteId: number, years: number[], params?: Record<string, any>): Promise<any> {
    const queryParams = new URLSearchParams({
      site_id: siteId.toString(),
      years: years.join(','),
      ...params
    });
    // Use fast endpoint with intelligent data manager for better performance and reliability
    const response = await this.fetchWithAuth(`/reports/dashboard/multi-year-fast/?${queryParams}`);
    return response.json();
  }

  async getCurrentVsHistoricalOptimized(siteId: number, currentYear?: number, params?: Record<string, any>): Promise<any> {
    const queryParams = new URLSearchParams({
      site_id: siteId.toString(),
      ...(currentYear && { current_year: currentYear.toString() }),
      ...params
    });
    const response = await this.fetchWithAuth(`/reports/current-vs-historical/current_vs_historical_optimized/?${queryParams}`);
    return response.json();
  }

  async getAvailableYears(siteId: number): Promise<any> {
    const queryParams = new URLSearchParams({
      site_id: siteId.toString()
    });
    const response = await this.fetchWithAuth(`/reports/current-vs-historical/available_years/?${queryParams}`);
    return response.json();
  }

  async getHistoricalStatistics(params: {
    site_id: number;
    period_type: string;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await this.fetchWithAuth(`/reports/historical-statistics/?${searchParams}`);
    return response.json();
  }

  // Meter Data API Methods
  async getMeterEnergyAnalysis(params: {
    site_id: number;
    start_date: string;
    end_date: string;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await this.fetchWithAuth(`/reports/meter-data/meter_energy_analysis/?${searchParams}`);
    return response.json();
  }

  async getMeterMultiYearComparison(siteId: number, years: number[]): Promise<any> {
    const searchParams = new URLSearchParams();
    searchParams.append('site_id', siteId.toString());
    searchParams.append('years', years.join(','));

    const response = await this.fetchWithAuth(`/reports/meter-data/meter_multi_year_comparison/?${searchParams}`);
    return response.json();
  }

  async getMeterCurrentVsHistorical(params: {
    site_id: number;
    current_year?: number;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await this.fetchWithAuth(`/reports/meter-data/meter_current_vs_historical/?${searchParams}`);
    return response.json();
  }

  async getMeterAvailableYears(siteId: number): Promise<any> {
    const searchParams = new URLSearchParams();
    searchParams.append('site_id', siteId.toString());

    const response = await this.fetchWithAuth(`/reports/meter-data/meter_available_years/?${searchParams}`);
    return response.json();
  }

  async getMeterStatistics(params: {
    site_id: number;
    period_type: string;
  }): Promise<any> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await this.fetchWithAuth(`/reports/meter-data/meter_statistics/?${searchParams}`);
    return response.json();
  }

  async getStatisticalAnalysis(siteId: number, periodType: 'monthly' | 'quarterly' | 'yearly' = 'monthly', params?: Record<string, any>): Promise<any> {
    const queryParams = new URLSearchParams({
      site_id: siteId.toString(),
      period_type: periodType,
      ...params
    });
    const response = await this.fetchWithAuth(`/reports/historical-data/statistical_analysis/?${queryParams}`);
    return response.json();
  }

  async getEnhancedEnergyAnalysis(
    startDate: string, 
    endDate: string, 
    options: {
      siteId?: number;
      comparePreviousYear?: boolean;
      includeTrends?: boolean;
    } = {}
  ): Promise<any> {
    const queryParams = new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
      compare_previous_year: (options.comparePreviousYear || false).toString(),
      include_trends: (options.includeTrends || false).toString()
    });
    
    // Add site_id if provided
    if (options.siteId) {
      queryParams.append('site_id', options.siteId.toString());
    }
    
    const response = await this.fetchWithAuth(`/reports/dashboard/energy-analysis/?${queryParams}`);
    return response.json();
  }

  async bulkCreateHistoricalData(siteId: number, dataSourceId: number, data: any[]): Promise<any> {
    const payload = {
      site_id: siteId,
      data_source_id: dataSourceId,
      data: data
    };
    const response = await this.fetchWithAuth('/reports/historical-data/bulk_create/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
    return response.json();
  }
}

export const reportingApi = new ReportingApiService();
